import { RpcError } from "grpc-web";
import { OmmuseClient } from "../proto/api_grpc_web_pb";
import { backOff } from "exponential-backoff";
import {
    CreateSupportTicketRequest,
    CreateSupportTicketResponse,
    GetSupportTicketsRequest,
    GetSupportTicketsResponse,
    UpdateTicketStatusRequest,
    UpdateTicketStatusResponse,
} from "../proto/api_pb";

const metadata = (token: string) => ({
    Authorization: token,
    "Access-Control-Allow-Origin": "*",
});

// gRPC status codes that represent a decision by the server rather than a
// transient fault. Retrying these changes nothing and hides the real error.
const NON_RETRYABLE_GRPC_CODES = new Set([
    3, // InvalidArgument
    7, // PermissionDenied
    16, // Unauthenticated
]);

export const isRetryableRpcError = (err: unknown): boolean => {
    const code =
        err !== null && typeof err === "object" && typeof (err as { code?: unknown }).code === "number"
            ? (err as { code: number }).code
            : null;
    return code === null || !NON_RETRYABLE_GRPC_CODES.has(code);
};

export const createSupportTicket = async (
    token: string,
    userId: string,
    category: string,
    subject: string,
    description: string,
    customCategory: string,
    screenshotUrl: string,
): Promise<CreateSupportTicketResponse | RpcError> => {
    const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
    const request = new CreateSupportTicketRequest();
    request.setUserId(userId);
    request.setCategory(category);
    request.setSubject(subject);
    request.setDescription(description);
    request.setCustomCategory(customCategory);
    request.setScreenshotUrl(screenshotUrl);
    return await backOff(
        () =>
            new Promise<CreateSupportTicketResponse | RpcError>((resolve, reject) => {
                ommuseClient.createSupportTicket(request, metadata(token), (err, response) => {
                    if (err) reject(err);
                    else resolve(response);
                });
            }),
        { numOfAttempts: 3, jitter: "full" }
    );
};

export const getSupportTickets = async (
    token: string,
    status: string,
    category: string,
    page: number,
    limit: number,
): Promise<GetSupportTicketsResponse | RpcError> => {
    const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
    const request = new GetSupportTicketsRequest();
    request.setStatus(status);
    request.setCategory(category);
    request.setPage(page);
    request.setLimit(limit);
    return await backOff(
        () =>
            new Promise<GetSupportTicketsResponse | RpcError>((resolve, reject) => {
                ommuseClient.getSupportTickets(request, metadata(token), (err, response) => {
                    if (err) reject(err);
                    else resolve(response);
                });
            }),
        { numOfAttempts: 3, jitter: "full" }
    );
};

export const updateTicketStatus = async (
    token: string,
    ticketId: string,
    status: string,
): Promise<UpdateTicketStatusResponse | RpcError> => {
    const ommuseClient = new OmmuseClient(process.env.REACT_APP_API_URL!);
    const request = new UpdateTicketStatusRequest();
    request.setTicketId(ticketId);
    request.setStatus(status);
    return await backOff(
        () =>
            new Promise<UpdateTicketStatusResponse | RpcError>((resolve, reject) => {
                ommuseClient.updateTicketStatus(request, metadata(token), (err, response) => {
                    if (err) reject(err);
                    else resolve(response);
                });
            }),
        {
            numOfAttempts: 3,
            jitter: "full",
            // Don't burn three round-trips on a verdict that cannot change.
            // PermissionDenied/Unauthenticated/InvalidArgument are decisions,
            // not transient faults — retrying only delays the error the user
            // needs to see.
            retry: (err) => isRetryableRpcError(err),
        }
    );
};
